import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { useOutletContext, useParams } from 'react-router-dom';
import {
  getDrawExport,
  portalErrorMessage,
  reportPortalActivity
} from '../../features/portal/portalService';

const PortalDrawExport = ({ mode }) => {
  const { eventId, venueId } = useParams();
  const { token } = useOutletContext();
  const [open, setOpen] = useState(false);
  const [scope, setScope] = useState('club');
  const [parts, setParts] = useState({ sections: true, bracket: true, teamList: true });
  const [pageMode, setPageMode] = useState('canvas');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [reload, setReload] = useState(0);
  const [exporting, setExporting] = useState('');

  useEffect(() => {
    if (!open || !token || !eventId) return undefined;

    let cancelled = false;
    setLoading(true);
    setError('');
    setData(null);

    const request =
      mode === 'secretary' && scope === 'club'
        ? { scope, venueId }
        : { scope };

    getDrawExport(token, request)
      .then((response) => {
        if (!cancelled) setData(response);
      })
      .catch((requestError) => {
        if (!cancelled) setError(portalErrorMessage(requestError));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, scope, reload, token, eventId, mode, venueId]);

  // Bracket games are in the payload for scores only; a section game is what
  // makes a draw (and its team list) available.
  const hasDraw = Boolean(data?.games?.some((game) => game.section));
  const hasBracket = Boolean(data?.bracket?.matches?.length);
  const hasContent = hasDraw || hasBracket;
  const hasSelection =
    (parts.sections && hasDraw) ||
    (parts.teamList && hasDraw) ||
    (parts.bracket && hasBracket);
  const canExport = Boolean(data) && hasContent && hasSelection && !loading && !exporting;

  const handleScopeChange = (event, value) => {
    if (value) setScope(value);
  };

  const handlePartChange = (part) => (event) => {
    setParts((current) => ({ ...current, [part]: event.target.checked }));
  };

  const handleExport = async (format) => {
    if (!canExport) return;

    setExporting(format);
    setError('');

    try {
      if (format === 'xlsx') {
        const { exportDrawXlsx } = await import('../../helpers/drawXlsxExport');
        exportDrawXlsx(data.event, data.games, {
          bracket: data.bracket,
          includeSections: parts.sections,
          includeTeamList: parts.teamList,
          includeBracket: parts.bracket
        });
      } else {
        const { exportDrawPdf } = await import('../../helpers/drawPdfExport');
        exportDrawPdf(data.event, data.games, {
          pageMode,
          bracket: data.bracket,
          includeSections: parts.sections,
          includeTeamList: parts.teamList,
          includeBracket: parts.bracket
        });
      }
      setOpen(false);

      const isClub = data.scope === 'club' && Boolean(data.club);
      const scopeLabel = isClub ? data.club.venueName || 'Club' : 'Whole event';
      const partLabels = [
        parts.sections && hasDraw ? 'Sections' : null,
        parts.bracket && hasBracket ? 'Bracket' : null,
        parts.teamList && hasDraw ? 'Team list' : null
      ].filter(Boolean);
      const gameCount = Array.isArray(data.games) ? data.games.length : 0;
      const pageLabel = format === 'pdf' ? ` · ${pageMode === 'a4' ? 'A4' : 'Canvas'}` : '';

      reportPortalActivity(
        token,
        {
          operation: 'draw.export',
          entityId: isClub ? data.club.venueId : data.event?.id || eventId,
          entityName: isClub ? data.club.venueName || 'Club' : data.event?.name || 'Whole event',
          summary: `${scopeLabel} · ${
            format === 'xlsx' ? 'XLSX' : 'PDF'
          }${pageLabel} · ${gameCount} game${gameCount === 1 ? '' : 's'} · ${partLabels.join(
            ', '
          )}`,
          metadata: {
            format,
            pageMode: format === 'pdf' ? pageMode : null,
            scope: data.scope,
            venueId: isClub ? data.club.venueId : null,
            gameCount,
            includeSections: Boolean(parts.sections && hasDraw),
            includeBracket: Boolean(parts.bracket && hasBracket),
            includeTeamList: Boolean(parts.teamList && hasDraw),
            bracketName: data.bracket?.name || null
          }
        },
        { eventId }
      );
    } catch {
      setError('The draw could not be exported. Please try again.');
    } finally {
      setExporting('');
    }
  };

  const emptyMessage =
    scope === 'club'
      ? 'No draw is available for this club yet.'
      : 'No draw is available for this event yet.';

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        startIcon={<DownloadIcon />}
        onClick={() => setOpen(true)}
        sx={{ whiteSpace: 'nowrap' }}>
        Export draw
      </Button>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="xs"
        fullWidth
        aria-labelledby="portal-draw-export-title">
        <DialogTitle id="portal-draw-export-title">Export Draw</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Choose the draw to export, then a format to download.
          </Typography>

          <Typography variant="subtitle2" sx={{ mt: 2 }}>
            Draw
          </Typography>
          <ToggleButtonGroup
            exclusive
            fullWidth
            size="small"
            value={scope}
            onChange={handleScopeChange}
            aria-label="Draw scope"
            sx={{ mt: 0.5 }}>
            <ToggleButton value="club">This club</ToggleButton>
            <ToggleButton value="event">Whole event</ToggleButton>
          </ToggleButtonGroup>

          <Box sx={{ mt: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={parts.sections}
                  disabled={!hasDraw}
                  onChange={handlePartChange('sections')}
                />
              }
              label="Sections"
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={parts.bracket}
                  disabled={!hasBracket}
                  onChange={handlePartChange('bracket')}
                />
              }
              label="Bracket"
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={parts.teamList}
                  disabled={!hasDraw}
                  onChange={handlePartChange('teamList')}
                />
              }
              label="Team List"
            />
          </Box>

          {!loading && data && (
            <Typography variant="caption" color="text.secondary" display="block">
              {hasBracket
                ? `Bracket: ${data.bracket?.name || 'Bracket'}`
                : 'No bracket found for this event.'}
            </Typography>
          )}

          {loading && (
            <Box display="flex" alignItems="center" justifyContent="center" gap={1} py={3}>
              <CircularProgress size={22} />
              <Typography variant="body2">Loading draw...</Typography>
            </Box>
          )}

          {!loading && error && (
            <Alert
              severity="error"
              sx={{ mt: 1 }}
              action={
                <Button color="inherit" size="small" onClick={() => setReload((value) => value + 1)}>
                  Retry
                </Button>
              }>
              {error}
            </Alert>
          )}

          {!loading && !error && data && !hasContent && (
            <Alert severity="info" sx={{ mt: 1 }}>
              {emptyMessage}
            </Alert>
          )}

          {!loading && !error && data && hasContent && !hasSelection && (
            <Typography variant="caption" color="warning.main" display="block" sx={{ mt: 1 }}>
              Choose at least one part to export.
            </Typography>
          )}

          <Stack spacing={1.5} mt={2}>
            <Button
              fullWidth
              variant="outlined"
              disabled={!canExport}
              onClick={() => handleExport('xlsx')}
              startIcon={
                exporting === 'xlsx' ? <CircularProgress size={18} /> : undefined
              }>
              Excel (.xlsx)
            </Button>

            <Box>
              <Typography variant="subtitle2">PDF page size</Typography>
              <ToggleButtonGroup
                exclusive
                fullWidth
                size="small"
                value={pageMode}
                onChange={(event, value) => value && setPageMode(value)}
                aria-label="PDF page size"
                sx={{ mt: 0.5 }}>
                <ToggleButton value="canvas">Canvas</ToggleButton>
                <ToggleButton value="a4">A4</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <Button
              fullWidth
              variant="outlined"
              disabled={!canExport}
              onClick={() => handleExport('pdf')}
              startIcon={
                exporting === 'pdf' ? <CircularProgress size={18} /> : undefined
              }>
              PDF (.pdf)
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} color="primary">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PortalDrawExport;
